import {
  breakpointsMediaQuery,
  getColor,
  useMediaQuery
} from '@/helpers/utils';

import BasicECharts from '@/components/common/BasicEChart';
import { PieChart } from 'echarts/charts';
import * as echarts from 'echarts/core';
import {
  GridComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { SiPwa } from 'react-icons/si';
import { useTooltipHide } from '@/hooks/useTooltipHide';
import { useAppContext } from '@/Main';
import type { Ref } from 'react';
import type { LighthouseCategory } from '@/app/PageSpeed/api/types/analysisApiTypes';

const colorByScore = (score: number): string =>
  score >= 90 ? 'success' : score >= 50 ? 'warning' : 'danger';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  PieChart,
  CanvasRenderer
]);

type CircularPieChartsProps = {
  chartRef?: Ref<unknown> | null;
  data?: Partial<LighthouseCategory> | null;
  sizes?: string;
  labelFontSize?: number;
};

const CircularPieCharts = ({
  chartRef,
  data,
  sizes = '6.625rem',
  labelFontSize = 23
}: CircularPieChartsProps) => {
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  const {
    config: { isDark }
  } = useAppContext() as unknown as { config: { isDark: boolean } };
  useTooltipHide(chartRef as never);

  if (!data || typeof data.score !== 'number') {
    return (
      <div
        style={{ width: sizes, height: sizes, borderRadius: '50%' }}
        className="d-flex align-items-center justify-content-center bg-200 text-muted"
      >
        <span className="fs--1">N/A</span>
      </div>
    );
  }

  if (data?.id === 'pwa') {
    return (
      <div
        style={{ width: sizes, height: sizes, borderRadius: '50%' }}
        className="d-flex align-items-center justify-content-center bg-200 text-1000"
      >
        <SiPwa className="fs-7" />
      </div>
    );
  }

  const scorePercent = Number((data.score * 100).toFixed(0));

  return (
    <BasicECharts
      ref={chartRef as never}
      echarts={echarts}
      options={
        getOptions(
          [
            {
              id: data?.id,
              value: scorePercent,
              color: colorByScore(scorePercent),
              name: data?.title
            },
            {
              id: '',
              value: 100 - scorePercent,
              color: 'gray-300',
              name: ''
            }
          ],
          ['100%', '77%'],
          scorePercent,
          labelFontSize,
          isMdUp,
          isDark
        ) as never
      }
      style={{ width: sizes, height: sizes }}
    />
  );
};

export default CircularPieCharts;

type ChartDatum = {
  id?: string;
  value: number;
  color: string;
  name?: string;
};

const getOptions = (
  data: ChartDatum[],
  radius: string | string[],
  percent: number,
  labelFontSize: number,
  isMdUp: boolean,
  isDark: boolean
) => ({
  textStyle: {
    fontFamily: 'dana'
  },
  color: data.map(item => getColor(item.color)),
  tooltip: {
    triggerOn: (isMdUp ? 'mousemove' : 'click') as
      | 'mousemove'
      | 'click'
      | 'mousemove|click'
      | 'none',
    padding: [7, 10],
    transitionDuration: 0,
    backgroundColor: getColor('gray-100'),
    borderColor: getColor('gray-300'),
    textStyle: { color: getColor('gray-1100'), fontFamily: 'dana !important' },
    borderWidth: 1,
    confine: true,
    show: false,
    formatter: (params: { data: ChartDatum; percent: number }) =>
      `<div><strong>${params.data.name}:</strong> ${params.percent}${labelFontSize < 15 ? '' : '%'}</div>`.toPersianDigit()
  },
  series: [
    {
      name: `${percent}${labelFontSize < 15 ? '' : '%'}`,
      type: 'pie' as const,
      radius,
      avoidLabelOverlap: false,
      emphasis: {
        scale: false
      },
      itemStyle: {
        borderWidth: 2,
        borderColor: getColor('gray-100')
      },
      label: {
        show: true,
        position: 'center' as const,
        formatter: '{a}',
        fontSize: labelFontSize,
        color: getColor(isDark ? 'gray-1100' : 'gray-900'),
        fontWeight: 600
      },
      data
    }
  ]
});
